import { useState } from 'react'
import { isAxiosError } from 'axios'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { applyFleetSeverityDefault } from './api'
import type { FleetSeverity } from './types'

/**
 * The user's fleet-wide default per severity (user_fleet_alarm_default):
 * personal, applies to every accessible site including ones gained later,
 * and is overridden by site severity rules and individual settings. Saves
 * immediately, as before. It has nothing to do with how alarm types are
 * classified (that is the Alarm Topics admin page).
 */
export default function SeverityDefaultsSection({ severities }: { severities: FleetSeverity[] }) {
  const queryClient = useQueryClient()
  const [message, setMessage] = useState<{ text: string; isError: boolean } | null>(null)
  // The value being saved, shown at once (set synchronously on change) and
  // cleared once the refreshed overview has arrived or the save failed.
  const [pending, setPending] = useState<{ id: number; isSubscribed: boolean } | null>(null)

  const mutation = useMutation({
    mutationFn: ({ id, isSubscribed }: { id: number; isSubscribed: boolean; name: string }) =>
      applyFleetSeverityDefault(id, isSubscribed),
    // Returning the refetch keeps the mutation pending (and the switch on its
    // new value) until the refreshed overview arrives, so it never flickers back.
    onSuccess: (_, { isSubscribed, name }) => {
      setMessage({ isError: false, text: `${name} default turned ${isSubscribed ? 'on' : 'off'} for all your sites.` })
      return queryClient.invalidateQueries({ queryKey: ['fleet-alarm-overview'] })
    },
    onSettled: () => setPending(null),
    onError: (err) => {
      const apiError = isAxiosError(err) ? (err.response?.data as { error?: string } | undefined)?.error : undefined
      setMessage({ isError: true, text: apiError || 'Failed to update the default. Nothing was changed.' })
    },
  })

  return (
    <section aria-labelledby="severity-defaults-heading" className="rounded-2xl border border-nav-line bg-card p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="severity-defaults-heading" className="text-base font-semibold text-ink">
          Severity defaults
        </h2>
        <span className="rounded-full bg-bg-tertiary px-2.5 py-0.5 text-xs font-semibold text-text-secondary">
          Your notifications · all your sites
        </span>
      </div>
      <p className="mt-1 text-sm text-text-secondary">
        Your starting point for each severity across every site you can access, including sites added later. It
        ignores the site selection above, and applies only where you have no severity subscription or individual
        setting for that site. Changes save immediately. This doesn’t change how alarms are classified.
      </p>
      <div className="mt-3 divide-y divide-nav-line rounded-2xl border border-nav-line">
        {severities.map((sev) => (
          <label
            key={sev.alarm_severity_id}
            className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
            data-testid={`default-${sev.alarm_severity_id}`}
          >
            <span className="text-text-primary">{sev.alarm_severity_name}</span>
            <span className="flex items-center gap-2">
              <span className="text-xs text-text-secondary">
                {sev.systems_affected}/{sev.total_systems} subscribed
              </span>
              <input
                type="checkbox"
                aria-label={`${sev.alarm_severity_name} default for all sites`}
                checked={pending?.id === sev.alarm_severity_id ? pending.isSubscribed : sev.is_default_subscribed}
                disabled={pending !== null}
                onChange={(e) => {
                  setPending({ id: sev.alarm_severity_id, isSubscribed: e.target.checked })
                  mutation.mutate({
                    id: sev.alarm_severity_id,
                    isSubscribed: e.target.checked,
                    name: sev.alarm_severity_name,
                  })
                }}
              />
            </span>
          </label>
        ))}
      </div>
      {message && (
        <p role="status" className={`mt-2 text-sm ${message.isError ? 'text-error' : 'text-brand'}`}>
          {message.text}
        </p>
      )}
    </section>
  )
}
